/**
 * L.A.B. Putter Configurator
 *
 * Companion JS to lab-configurator.liquid. Activates on products with the
 * custom.lab_configurator_template metafield set (OZ.1 / OZ.1i / DF3 /
 * DF3i / DF2.1 / MEZZ.1 / MEZZ.1 MAX / OZ.1i HS / LINK.1 / LINK.2.1 /
 * LINK.2.2).
 *
 * Differences from the iron / driver configurator (custom-fit-configurator.js):
 *
 *   - Single-club mode only — qty is always 1, no club checkboxes, no
 *     "select clubs to continue" gate.
 *   - More upgrade categories (Putting Style, Shaft, Grip, Headcover, Laser
 *     Personalisation) so the price summary shows / hides rows dynamically by
 *     `data-upgrade-key` and the cart submission can include up to 5 surcharge
 *     line items (vs. the 2 in iron / driver mode).
 *   - One combined surcharge product (custom-fit-lab-upgrade) with one variant
 *     per price tier, vs. the two separate shaft / grip surcharge products on
 *     other categories. JS picks the matching variant ID by price-in-pence.
 *   - Conditional Personalisation Text input — appears when Laser
 *     Personalisation = Yes, validated only when visible.
 *   - Acknowledgement checkbox required before Add to Basket enables.
 *   - No flex dependent dropdown — putters don't have flex.
 *   - Progressive field enabling — each dropdown unlocks the next when
 *     selected, matching the iron / driver configurator. The Personalisation
 *     Text <input> and Acknowledgement checkbox are not selects so they
 *     skip the cascade; the checkbox is gated separately via isFormReady().
 *   - Image-rendering dropdowns — Colour, Alignment Marking, Headcover render
 *     a small icon to the LEFT of the option text; Shaft and Grip render a
 *     full-width banner ABOVE the option text. Driven by `data-image` on each
 *     <option> and the parent .config-field's layout class
 *     (--icon-left or --banner). Alignment Marking adds an --icon-large
 *     size modifier to bump the 32px default icon up to 72px.
 *   - Combo rules engine (initComboEngine) — reads template.combos JSON
 *     from data-lab-combos and applies putting-style and shaft-lean-driven
 *     defaults + grip filtering. See section near the bottom.
 *
 * Reused patterns (carried over verbatim or near-verbatim):
 *
 *   - Hide-variant-titles in cart drawer (initCartDrawerHooks).
 *   - updateMainPrice() — separate price element above the standard product
 *     price to avoid Alpine.js reactivity conflicts.
 *   - findSurchargeVariantId() — price-tier matching with fallback.
 *   - generateConfigGroupId() — groups all line items from one configuration.
 *   - Custom dropdown enhancement (buildSingleCustomDropdown) — same floating
 *     label / option panel pattern; extended below to render images.
 *   - Tab switching — hides theme variant_picker / buy_buttons /
 *     inventory_status / estimate_delivery on Custom Fit tab; restores on
 *     Standard.
 *   - sessionStorage 'openCartDrawer' flag — reload then auto-open drawer.
 */

(function() {
  'use strict';

  const configuratorEl = document.querySelector('[data-lab-configurator]');

  // ============================================================
  // CART DISPLAY: Rebrand Custom Fit line items
  // ============================================================
  //
  // Custom Fit putters are added to cart against a hidden "Custom Fit - LAB
  // Build" product variant (so the real putter's stock isn't affected).
  // That means the cart drawer renders the build product's image and title
  // by default ("Custom Fit - LAB Build"), which is confusing for the
  // customer.
  //
  // We pass the original putter's handle and title as `_product_handle`
  // and `_product_title` line item properties at add-to-cart time. Shopify
  // hides underscored properties from the UI, but they're still in cart.js
  // data. We:
  //   1. Fetch /cart.js to get the line item properties
  //   2. For each Custom Fit item, fetch the original product's featured
  //      image via /products/<handle>.js (cached)
  //   3. Replace the line item's <img> src and title text with the
  //      original putter's image + "<title> - Custom Fit"

  var productImageCache = {};

  function fetchProductImage(handle) {
    if (productImageCache[handle]) {
      return Promise.resolve(productImageCache[handle]);
    }
    return fetch('/products/' + encodeURIComponent(handle) + '.js')
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(p) {
        if (!p) return null;
        var url = p.featured_image || (p.images && p.images[0]) || null;
        if (url && url.indexOf('//') === 0) url = 'https:' + url;
        productImageCache[handle] = url;
        return url;
      })
      .catch(function() { return null; });
  }

  function rebrandCustomFitLineItem(domItem, handle, title) {
    if (!domItem || domItem.dataset.customFitRebranded === 'true') return;
    domItem.dataset.customFitRebranded = 'true';

    var newTitle = title + ' - Custom Fit';

    // Replace title. Cart drawer typically renders "Custom Fit - LAB Build"
    // wrapped in a bold element (e.g. <a><strong>CUSTOM FIT - LAB BUILD</strong></a>).
    // We need to fully replace the content — not just insert text — and
    // preserve the bold styling so the new title looks the same as the original.
    var titleEls = domItem.querySelectorAll('a, h1, h2, h3, h4, h5, [class*="title"], [class*="name"]');
    for (var i = 0; i < titleEls.length; i++) {
      var el = titleEls[i];
      var text = (el.textContent || '').trim().toUpperCase();
      if (text.indexOf('LAB BUILD') === -1) continue;

      // Look for the inner bold wrapper. Most likely a <strong>, <b>, or
      // an element with a 'bold' class. If found, we clone its tag+attributes
      // and reuse it with the new text so the bold styling is preserved.
      var boldWrapper = el.querySelector('strong, b, [class*="bold"]');

      // If no explicit bold tag, find any leaf descendant whose text matches
      // "LAB BUILD" — fallback for unusual markup
      if (!boldWrapper) {
        var allDescendants = el.querySelectorAll('*');
        for (var d = 0; d < allDescendants.length; d++) {
          var node = allDescendants[d];
          if (node.children.length === 0 &&
              (node.textContent || '').toUpperCase().indexOf('LAB BUILD') > -1) {
            boldWrapper = node;
            break;
          }
        }
      }

      if (boldWrapper) {
        // Clone the wrapper (preserves tag + classes + attrs) and set new text.
        // Then fully clear the title element and re-append the new wrapper.
        var newWrapper = boldWrapper.cloneNode(false);
        newWrapper.textContent = newTitle;
        el.innerHTML = '';
        el.appendChild(newWrapper);
      } else {
        // No nested wrapper — title element itself holds the text directly
        el.textContent = newTitle;
      }
      break;
    }

    // Replace image. We fetch the original product's featured image and
    // swap the src. Cleared srcset to prevent responsive overrides.
    fetchProductImage(handle).then(function(url) {
      if (!url) return;
      var img = domItem.querySelector('img');
      if (img) {
        img.src = url;
        if (img.srcset) img.srcset = '';
        img.removeAttribute('data-src');
      }
    });
  }

  function rebrandAllCustomFitItems() {
    fetch('/cart.js')
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(cart) {
        if (!cart) return;

        // Walk cart.items in order; collect those that need rebranding
        var customFitItems = [];
        cart.items.forEach(function(item) {
          var props = item.properties || {};
          if (props._config_type !== 'club') return;
          if (!props._product_handle || !props._product_title) return;
          customFitItems.push({
            key: item.key,
            handle: props._product_handle,
            title: props._product_title,
          });
        });
        if (customFitItems.length === 0) return;

        // Find DOM line items and match. First try matching by data-key
        // (most reliable when present); fall back to ordinal position
        // amongst items containing "Custom Fit" text.
        var allDomItems = document.querySelectorAll(
          '[data-cart-item-line], [class*="cart-item__wrapper"], [class*="cart-drawer__item"], [class*="line-item__"], [class*="mini-cart__item"]'
        );

        var customFitDomItems = [];
        Array.prototype.forEach.call(allDomItems, function(domItem) {
          var text = domItem.textContent || '';
          if (text.indexOf('Fit Type') !== -1 && text.indexOf('Custom Fit') !== -1) {
            customFitDomItems.push(domItem);
          }
        });

        // Match by data-key first
        var unmatchedDom = [];
        var matchedKeys = {};
        customFitDomItems.forEach(function(domItem) {
          var key = domItem.dataset.cartItemKey || domItem.dataset.key
            || domItem.getAttribute('data-key') || domItem.getAttribute('data-cart-item-key');
          if (!key) {
            var keyed = domItem.querySelector('[data-cart-item-key], [data-key]');
            if (keyed) {
              key = keyed.dataset.cartItemKey || keyed.dataset.key
                || keyed.getAttribute('data-key') || keyed.getAttribute('data-cart-item-key');
            }
          }
          if (key) {
            for (var i = 0; i < customFitItems.length; i++) {
              if (customFitItems[i].key === key) {
                rebrandCustomFitLineItem(domItem, customFitItems[i].handle, customFitItems[i].title);
                matchedKeys[key] = true;
                return;
              }
            }
          }
          unmatchedDom.push(domItem);
        });

        // Fallback: match remaining unmatched DOM items by ordinal position
        // amongst remaining unmatched cart items. Imperfect but workable.
        var unmatchedCart = customFitItems.filter(function(c) { return !matchedKeys[c.key]; });
        unmatchedDom.forEach(function(domItem, idx) {
          if (idx < unmatchedCart.length) {
            rebrandCustomFitLineItem(domItem, unmatchedCart[idx].handle, unmatchedCart[idx].title);
          }
        });
      })
      .catch(function() {});
  }

  // ============================================================
  // CART DISPLAY: Hide variant titles for Custom Fit line items
  // ============================================================

  function getDirectText(el) {
    var s = '';
    for (var i = 0; i < el.childNodes.length; i++) {
      if (el.childNodes[i].nodeType === 3) s += el.childNodes[i].textContent;
    }
    return s.trim();
  }

  function hideCustomFitVariantTitles() {
    var lineItems = document.querySelectorAll(
      '[data-cart-item-line], [class*="cart-item__wrapper"], [class*="cart-drawer__item"], [class*="line-item__"], [class*="mini-cart__item"]'
    );

    lineItems.forEach(function(item) {
      if (item.dataset.customFitProcessed === 'true') return;

      var text = item.textContent || '';
      if (text.indexOf('Fit Type') === -1 || text.indexOf('Custom Fit') === -1) return;

      item.querySelectorAll('*').forEach(function(el) {
        if (el === item) return;
        if (el.closest('[data-hidden-by-custom-fit]')) return;

        var tag = el.tagName.toLowerCase();
        if (tag.match(/^(h1|h2|h3|h4|h5|h6|button|svg|path|img|input|select|textarea)$/)) return;
        if (el.closest('h1, h2, h3, h4, button')) return;

        var direct = getDirectText(el);
        if (!direct || direct.length === 0 || direct.length > 200) return;
        if (direct.indexOf(' / ') === -1) return;

        var row = el.closest(
          '[class*="config-group-"]:not([class*="config-group-remove"]), ' +
          '[class*="cart-drawer-item__property"], dl, li, [class*="meta"], p'
        );
        if (!row || row === item || row.contains(item)) {
          row = el.parentElement && el.parentElement !== item ? el.parentElement : el;
        }

        row.style.display = 'none';
        row.dataset.hiddenByCustomFit = 'true';
      });

      item.dataset.customFitProcessed = 'true';
    });
  }

  function initCartDrawerHooks() {
    hideCustomFitVariantTitles();
    rebrandAllCustomFitItems();

    var debounceTimer = null;
    var observer = new MutationObserver(function(mutations) {
      var relevant = false;
      for (var i = 0; i < mutations.length; i++) {
        var m = mutations[i];
        if (m.type !== 'childList') continue;
        if (m.addedNodes.length === 0) continue;
        for (var j = 0; j < m.addedNodes.length; j++) {
          var n = m.addedNodes[j];
          if (n.nodeType !== 1) continue;
          var cls = (n.className && n.className.toString && n.className.toString()) || '';
          if (cls.indexOf('cart') > -1 || cls.indexOf('item') > -1 || cls.indexOf('line') > -1) {
            relevant = true; break;
          }
          if (n.querySelector && n.querySelector('[class*="cart"]')) {
            relevant = true; break;
          }
        }
        if (relevant) break;
      }
      if (!relevant) return;

      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(function() {
        document.querySelectorAll('[data-custom-fit-processed]').forEach(function(el) {
          if (!el.querySelector('[data-hidden-by-custom-fit]')) {
            delete el.dataset.customFitProcessed;
          }
        });
        hideCustomFitVariantTitles();
        rebrandAllCustomFitItems();
      }, 100);
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCartDrawerHooks);
  } else {
    initCartDrawerHooks();
  }

  if (!configuratorEl) return;

  // ============================================================
  // CONFIGURATION
  // ============================================================

  const CONFIG = {
    basePricePence: parseInt(configuratorEl.dataset.basePricePence || '0', 10),
    baseVariantId: configuratorEl.dataset.baseVariantId || '',
    buildVariantId: configuratorEl.dataset.labBuildVariantId || '',
    surchargeVariants: JSON.parse(configuratorEl.dataset.labSurchargeVariants || '{}'),
    premiumPence: parseInt(configuratorEl.dataset.labPremiumPence || '0', 10),
    premiumVariants: JSON.parse(configuratorEl.dataset.labPremiumVariants || '{}'),
    productName: configuratorEl.dataset.productName || '',
    showPersonalisation: configuratorEl.dataset.showPersonalisation === 'true',
    currencySymbol: '£'
  };

  let isCustomTab = false;

  // ============================================================
  // STATE
  // ============================================================

  const state = {
    dexterity: '',
    putting_style: null,
    colour: '',
    riser_colour: '',
    shaft: null,
    length: '',
    lie: '',
    alignment_front: '',
    alignment_back: '',
    head_weight: '',
    shaft_lean: '',
    grip: null,
    headcover: null,
    laser: null,
    personalisation_text: '',
    acknowledgement: false
  };

  // ============================================================
  // PRICE CALCULATION
  // ============================================================

  function calculateTotal() {
    var base = CONFIG.basePricePence;
    var upgrades = {
      putting_style: state.putting_style ? state.putting_style.pricePence : 0,
      shaft:         state.shaft         ? state.shaft.pricePence         : 0,
      grip:          state.grip          ? state.grip.pricePence          : 0,
      headcover:     state.headcover     ? state.headcover.pricePence     : 0,
      laser:         state.laser         ? state.laser.pricePence         : 0
    };
    var upgradeTotal = upgrades.putting_style + upgrades.shaft + upgrades.grip
                     + upgrades.headcover + upgrades.laser;
    return {
      basePrice: base,
      premium: CONFIG.premiumPence,
      upgrades: upgrades,
      upgradeTotal: upgradeTotal,
      grandTotal: base + CONFIG.premiumPence + upgradeTotal
    };
  }

  function formatMoney(pence) {
    return (pence / 100).toFixed(2);
  }

  // ============================================================
  // MAIN PRICE DISPLAY
  // ============================================================

  function updateMainPrice(pence) {
    var customPrice = document.getElementById('configurator-custom-price');
    if (!customPrice) {
      var priceWrapper = document.querySelector('.product-info__price-wrapper');
      if (!priceWrapper) return;
      customPrice = document.createElement('div');
      customPrice.id = 'configurator-custom-price';
      customPrice.className = 'product-info__price-wrapper anm-fade-element mb-2 md:mb-2.5 is-visible';
      customPrice.innerHTML = '<div><div><p class="h3 price-wrapper h2"><span class="main-price">' + CONFIG.currencySymbol + '0.00</span></p></div></div>';
      priceWrapper.parentNode.insertBefore(customPrice, priceWrapper.nextSibling);
    }

    customPrice.style.display = '';
    customPrice.querySelector('.main-price').textContent = CONFIG.currencySymbol + formatMoney(pence);
  }

  function restoreMainPrice() {
    var customPrice = document.getElementById('configurator-custom-price');
    if (customPrice) customPrice.style.display = 'none';
  }

  // ============================================================
  // UI UPDATES
  // ============================================================

  function updatePriceDisplay() {
    var totals = calculateTotal();

    document.querySelectorAll('[data-upgrade-row]').forEach(function(row) {
      var key = row.dataset.upgradeKey;
      var pence = totals.upgrades[key] || 0;
      if (pence > 0) {
        row.style.display = '';
        var priceEl = row.querySelector('[data-upgrade-price]');
        if (priceEl) priceEl.textContent = formatMoney(pence);
      } else {
        row.style.display = 'none';
      }
    });

    var totalEl = document.querySelector('[data-total-price]');
    if (totalEl) totalEl.textContent = formatMoney(totals.grandTotal);

    if (isCustomTab) {
      updateMainPrice(totals.grandTotal);
    }

    updateAddButton();
  }

  function updateAddButton() {
    var addBtn = document.querySelector('[data-custom-fit-add]');
    if (!addBtn) return;

    var totals = calculateTotal();
    var ready = isFormReady();

    addBtn.disabled = !ready;

    if (ready) {
      addBtn.textContent = 'Add to Basket — ' + CONFIG.currencySymbol + formatMoney(totals.grandTotal);
    } else if (!state.acknowledgement) {
      addBtn.textContent = 'Confirm acknowledgement to continue';
    } else {
      addBtn.textContent = 'Complete all selections to continue';
    }
  }

  function isFormReady() {
    if (!state.acknowledgement) return false;

    // Check each field only if it's actually rendered on the page.
    // Different L.A.B. templates show different fields (e.g. LINK.1 has no
    // Colour, Putting Style, or Alignment Back; some have no Head Weight).
    // The presence of the <select> element in the DOM is the source of truth.

    if (!state.dexterity) return false;

    if (document.querySelector('[data-custom-putting-style]') && !state.putting_style) return false;
    if (document.querySelector('[data-custom-colour]')        && !state.colour)        return false;
    if (document.querySelector('[data-custom-riser-colour]')  && !state.riser_colour)  return false;

    if (!state.shaft) return false;
    if (!state.length) return false;
    if (!state.lie) return false;

    if (document.querySelector('[data-custom-alignment-front]') && !state.alignment_front) return false;
    if (document.querySelector('[data-custom-alignment-back]')  && !state.alignment_back)  return false;
    if (document.querySelector('[data-custom-head-weight]')     && !state.head_weight)     return false;
    if (document.querySelector('[data-custom-shaft-lean]')      && !state.shaft_lean)      return false;

    if (!state.grip) return false;
    if (!state.headcover) return false;

    if (CONFIG.showPersonalisation) {
      if (!state.laser) return false;
      if (state.laser.pricePence > 0 && !state.personalisation_text) return false;
    }

    return true;
  }

  // ============================================================
  // OPTION SELECTION
  // ============================================================

  function getSelectedOptionData(selectEl) {
    if (!selectEl) return null;
    var opt = selectEl.options[selectEl.selectedIndex];
    if (!opt || !opt.value) return null;
    return {
      name: opt.dataset.optionName || opt.value,
      pricePence: parseInt(opt.dataset.pricePence || '0', 10),
      handle: opt.dataset.handle || ''
    };
  }

  function wireDropdown(selector, stateKey, isPriced, onChangeExtra) {
    var el = document.querySelector(selector);
    if (!el) return;

    function syncFromElement() {
      if (isPriced) {
        state[stateKey] = getSelectedOptionData(el);
      } else {
        state[stateKey] = el.value || '';
      }
      if (typeof onChangeExtra === 'function') onChangeExtra();
      updatePriceDisplay();
    }

    syncFromElement();
    el.addEventListener('change', syncFromElement);
  }

  function initOptionSelection() {
    wireDropdown('[data-custom-dexterity]',       'dexterity',       false);
    wireDropdown('[data-custom-putting-style]',   'putting_style',   true);
    wireDropdown('[data-custom-colour]',          'colour',          false);
    wireDropdown('[data-custom-riser-colour]',    'riser_colour',    false);
    wireDropdown('[data-shaft-option]',           'shaft',           true);
    wireDropdown('[data-custom-length]',          'length',          false);
    wireDropdown('[data-custom-lie]',             'lie',             false);
    wireDropdown('[data-custom-alignment-front]', 'alignment_front', false);
    wireDropdown('[data-custom-alignment-back]',  'alignment_back',  false);
    wireDropdown('[data-custom-head-weight]',     'head_weight',     false);
    wireDropdown('[data-custom-shaft-lean]',      'shaft_lean',      false);
    wireDropdown('[data-grip-option]',            'grip',            true);
    wireDropdown('[data-custom-headcover]',       'headcover',       true);
    wireDropdown('[data-custom-laser]',           'laser',           true, togglePersonalisationText);
  }

  // ============================================================
  // PERSONALISATION TEXT
  // ============================================================

  function togglePersonalisationText() {
    var wrapper = document.querySelector('[data-personalisation-text-wrapper]');
    if (!wrapper) return;

    var shouldShow = state.laser && state.laser.pricePence > 0;
    wrapper.style.display = shouldShow ? '' : 'none';

    if (!shouldShow) {
      var input = document.querySelector('[data-custom-personalisation-text]');
      if (input) input.value = '';
      state.personalisation_text = '';
    }
  }

  function initPersonalisationText() {
    var input = document.querySelector('[data-custom-personalisation-text]');
    if (!input) return;
    input.addEventListener('input', function() {
      var upper = this.value.toUpperCase();
      if (this.value !== upper) this.value = upper;
      state.personalisation_text = upper;
      updateAddButton();
    });
  }

  // ============================================================
  // ACKNOWLEDGEMENT
  // ============================================================

  function initAcknowledgement() {
    var checkbox = document.querySelector('[data-custom-acknowledgement]');
    if (!checkbox) return;
    state.acknowledgement = checkbox.checked;
    checkbox.addEventListener('change', function() {
      state.acknowledgement = this.checked;
      updateAddButton();
    });
  }

  // ============================================================
  // ADD TO CART
  // ============================================================

  function findSurchargeVariantId(pricePence, variantMap) {
    if (pricePence <= 0 || !variantMap || Object.keys(variantMap).length === 0) return null;
    if (variantMap[pricePence.toString()]) return variantMap[pricePence.toString()];
    var availablePrices = Object.keys(variantMap).map(Number).sort(function(a, b) { return a - b; });
    for (var i = 0; i < availablePrices.length; i++) {
      if (availablePrices[i] >= pricePence) return variantMap[availablePrices[i].toString()];
    }
    return variantMap[availablePrices[availablePrices.length - 1].toString()];
  }

  function generateConfigGroupId() {
    return 'cf_' + Date.now().toString(36) + '_' + Math.random().toString(36).substr(2, 5);
  }

  function buildCartItems() {
    // We add the LAB Build variant (hidden, no inventory) to cart instead
    // of the real product variant. The real product's stock is never touched
    // by custom orders. The Standard tab continues to add the real variant.
    if (!CONFIG.buildVariantId) {
      console.error('No L.A.B. build variant ID configured — product is missing custom.lab_build_variant_id metafield');
      return [];
    }

    var items = [];
    var groupId = generateConfigGroupId();

    // Core properties always present
    var properties = {
      '_config_group': groupId,
      '_config_type': 'club',
      '_product_handle': window.location.pathname.split('/products/').pop().split('/')[0].split('?')[0],
      '_product_title': CONFIG.productName,
      'Fit Type': 'Custom Fit',
      'Dexterity': state.dexterity,
      'Shaft': state.shaft.name,
      'Length': state.length + '"',
      'Lie': state.lie,
      'Grip': state.grip.name,
      'Headcover': state.headcover.name
    };

    // Conditionally rendered fields — only add to cart properties if the
    // field exists on this template's configurator. Mirrors isFormReady().
    if (document.querySelector('[data-custom-putting-style]') && state.putting_style) {
      properties['Putting Style'] = state.putting_style.name;
    }
    if (document.querySelector('[data-custom-colour]') && state.colour) {
      properties['Colour'] = state.colour;
    }
    if (document.querySelector('[data-custom-riser-colour]') && state.riser_colour) {
      properties['Riser Colour'] = state.riser_colour;
    }
    if (document.querySelector('[data-custom-head-weight]') && state.head_weight) {
      properties['Head Weight'] = state.head_weight;
    }

    var alignmentBackEl = document.querySelector('[data-custom-alignment-back]');
    if (alignmentBackEl) {
      properties['Alignment Marking - Front'] = state.alignment_front;
      properties['Alignment Marking - Back']  = state.alignment_back;
    } else if (document.querySelector('[data-custom-alignment-front]')) {
      properties['Alignment Marking'] = state.alignment_front;
    }

    var shaftLeanEl = document.querySelector('[data-custom-shaft-lean]');
    if (shaftLeanEl) properties['Shaft Lean'] = state.shaft_lean;

    if (CONFIG.showPersonalisation && state.laser) {
      properties['Laser Personalisation'] = state.laser.name;
      if (state.laser.pricePence > 0 && state.personalisation_text) {
        properties['Personalisation Text'] = state.personalisation_text;
      }
    }

    items.push({
      id: parseInt(CONFIG.buildVariantId, 10),
      quantity: 1,
      properties: properties
    });

    var surcharges = [
      { state: state.putting_style, label: 'Putting Style' },
      { state: state.shaft,         label: 'Shaft' },
      { state: state.grip,          label: 'Grip' },
      { state: state.headcover,     label: 'Headcover' },
      { state: state.laser,         label: 'Laser Personalisation' }
    ];

    surcharges.forEach(function(s) {
      if (!s.state || s.state.pricePence <= 0) return;
      var variantId = findSurchargeVariantId(s.state.pricePence, CONFIG.surchargeVariants);
      if (!variantId) {
        console.error('No surcharge variant for ' + s.label + ' at ' + s.state.pricePence + 'p');
        return;
      }
      items.push({
        id: parseInt(variantId, 10),
        quantity: 1,
        properties: {
          '_config_group': groupId,
          '_config_type': s.label.toLowerCase().replace(/\s+/g, '_') + '_surcharge',
          '_surcharge_label': s.label + ': ' + s.state.name
        }
      });
    });

    // ----------------------------------------------------------
    // Custom Fit Premium — fixed per-template surcharge added to
    // every custom-fit cart. Uses dedicated variants on the
    // Custom Fit - LAB Premium product. Variant IDs are mapped
    // directly by price-pence: no fuzzy fallback needed (template
    // values are always one of 50/60/80/100).
    // ----------------------------------------------------------
    if (CONFIG.premiumPence > 0 && CONFIG.premiumVariants) {
      var premiumVariantId = CONFIG.premiumVariants[CONFIG.premiumPence.toString()];
      if (!premiumVariantId) {
        console.error('No LAB premium variant for ' + CONFIG.premiumPence + 'p — check template custom_fit_premium value');
      } else {
        items.push({
          id: parseInt(premiumVariantId, 10),
          quantity: 1,
          properties: {
            '_config_group': groupId,
            '_config_type': 'lab_premium_surcharge',
            '_surcharge_label': 'Custom Fit Premium'
          }
        });
      }
    }

    return items;
  }

  function addToCart() {
    if (!isFormReady()) {
      document.querySelectorAll('.config-field select').forEach(function(field) {
        if (field.disabled) return;
        var dd = field.closest('.config-field').querySelector('.custom-dropdown');
        if (!field.value) {
          if (dd) dd.classList.add('is-invalid');
        } else {
          if (dd) dd.classList.remove('is-invalid');
        }
      });
      return;
    }

    var items = buildCartItems();
    if (items.length === 0) return;

    var addBtn = document.querySelector('[data-custom-fit-add]');
    if (addBtn) { addBtn.disabled = true; addBtn.textContent = 'Adding...'; }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: items })
    })
    .then(function(response) {
      if (!response.ok) return response.json().then(function(err) { throw new Error(err.description || 'Failed'); });
      return response.json();
    })
    .then(function() {
      if (addBtn) addBtn.textContent = '✓ Added to Basket!';
      sessionStorage.setItem('openCartDrawer', 'true');
      setTimeout(function() { window.location.reload(); }, 300);
    })
    .catch(function(error) {
      console.error('L.A.B. add to cart error:', error);
      if (addBtn) {
        addBtn.textContent = 'Error — try again';
        addBtn.disabled = false;
      }
    });
  }

  // ============================================================
  // TAB SWITCHING
  // ============================================================

  function initTabs() {
    var tabs = document.querySelectorAll('[data-tab]');
    var panels = document.querySelectorAll('[data-tab-panel]');

    tabs.forEach(function(tab) {
      tab.addEventListener('click', function() {
        var target = this.dataset.tab;

        tabs.forEach(function(t) { t.classList.remove('active'); });
        this.classList.add('active');

        panels.forEach(function(panel) {
          if (panel.dataset.tabPanel === target) {
            panel.style.display = '';
            panel.classList.add('active');
          } else {
            panel.style.display = 'none';
            panel.classList.remove('active');
          }
        });

        if (target === 'custom') {
          isCustomTab = true;
          document.body.classList.add('configurator-custom-active');
          document.querySelectorAll('.product-info__buy-buttons-wrapper, .product-info__variant-picker, .product-info__inventory-status-wrapper, .product-info__estimate-delivery-wrapper').forEach(function(el) {
            el.style.display = 'none';
          });
          document.querySelectorAll('.product-info__text-with-icon_wrapper').forEach(function(el) {
            var text = el.textContent.toLowerCase();
            if (text.indexOf('in stock') > -1 || text.indexOf('dispatched within') > -1) {
              el.style.display = 'none';
              el.dataset.hiddenByConfigurator = 'true';
            }
          });
          updatePriceDisplay();
        } else {
          isCustomTab = false;
          document.body.classList.remove('configurator-custom-active');
          document.querySelectorAll('.product-info__buy-buttons-wrapper, .product-info__variant-picker, .product-info__inventory-status-wrapper').forEach(function(el) {
            el.style.display = '';
          });
          restoreMainPrice();
        }
      });
    });
  }

  // ============================================================
  // CUSTOM STYLED DROPDOWNS
  // ============================================================
  //
  // Replaces native <select> elements with a styled trigger / options panel.
  // Native select stays in the DOM (.custom-dropdown-hidden) as source of
  // truth for change events.
  //
  // Image rendering (Colour, Alignment, Headcover, Shaft, Grip):
  //   Each <option> can carry a `data-image` URL. When present, the custom
  //   option div gets an <img> child. The parent .config-field's layout
  //   class determines positioning:
  //     - .config-field--icon-left → 32px square, left of name
  //     - .config-field--banner    → full-width, above name
  //   The selected option's image also surfaces in the closed trigger via
  //   a .custom-dropdown__preview element appended after the trigger row.

  var arrowSVG = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#999" stroke-width="2"><path d="M6 9l6 6 6-6"/></svg>';

  function buildSingleCustomDropdown(select) {
    var field = select.closest('.config-field');
    if (!field) return;

    // Detect layout style from parent class
    var layout = 'text';
    if (field.classList.contains('config-field--icon-left')) layout = 'icon-left';
    else if (field.classList.contains('config-field--banner')) layout = 'banner';

    // Detect size modifier (currently only --icon-large supported)
    var sizeModifier = field.classList.contains('config-field--icon-large') ? 'icon-large' : '';

    var labelEl = field.querySelector('.config-label');
    var labelText = '';
    if (labelEl) {
      labelText = labelEl.textContent.trim();
      field.dataset.configLabel = labelText;
      labelEl.remove();
    } else {
      labelText = field.dataset.configLabel || '';
    }

    var dropdown = document.createElement('div');
    dropdown.className = 'custom-dropdown custom-dropdown--' + layout;
    if (sizeModifier) dropdown.className += ' custom-dropdown--' + sizeModifier;
    if (select.disabled) dropdown.classList.add('is-disabled');

    var trigger = document.createElement('div');
    trigger.className = 'custom-dropdown__trigger';

    // Trigger image — used by icon-left layouts to show the
    // selected option's icon to the LEFT of the label/value block
    var triggerImg = null;
    if (layout === 'icon-left') {
      triggerImg = document.createElement('span');
      triggerImg.className = 'custom-dropdown__trigger-icon';
      trigger.appendChild(triggerImg);
    }

    var triggerText = document.createElement('span');
    triggerText.className = 'custom-dropdown__trigger-text';

    var label = document.createElement('span');
    label.className = 'custom-dropdown__label';
    label.textContent = labelText;

    var value = document.createElement('span');
    value.className = 'custom-dropdown__value';

    triggerText.appendChild(label);
    triggerText.appendChild(value);
    trigger.appendChild(triggerText);

    var arrow = document.createElement('span');
    arrow.className = 'custom-dropdown__arrow';
    arrow.innerHTML = arrowSVG;
    trigger.appendChild(arrow);

    // Banner preview — used by banner layout to show the selected option's
    // image as a full-width banner BELOW the trigger
    var bannerPreview = null;
    if (layout === 'banner') {
      bannerPreview = document.createElement('div');
      bannerPreview.className = 'custom-dropdown__banner-preview';
      bannerPreview.style.display = 'none';
    }

    var optionsPanel = document.createElement('div');
    optionsPanel.className = 'custom-dropdown__options';

    Array.from(select.options).forEach(function(opt) {
      if (opt.disabled || opt.hidden || !opt.value) return;

      var optionDiv = document.createElement('div');
      optionDiv.className = 'custom-dropdown__option';
      optionDiv.dataset.value = opt.value;

      var imageUrl = opt.dataset.image || '';
      if (imageUrl) optionDiv.dataset.image = imageUrl;

      var optText = opt.textContent.trim();
      var priceMatch = optText.match(/(\(\+£[\d.]+\))/);
      var optName = priceMatch ? optText.replace(priceMatch[1], '').trim() : optText;

      // For banner layout, image goes ABOVE the text content
      if (layout === 'banner' && imageUrl) {
        var bannerImg = document.createElement('img');
        bannerImg.className = 'custom-dropdown__option-banner';
        bannerImg.src = imageUrl;
        bannerImg.alt = optName;
        bannerImg.loading = 'lazy';
        optionDiv.appendChild(bannerImg);
      }

      // For icon-left layout, image goes BEFORE the text content
      if (layout === 'icon-left' && imageUrl) {
        var iconImg = document.createElement('img');
        iconImg.className = 'custom-dropdown__option-icon';
        iconImg.src = imageUrl;
        iconImg.alt = optName;
        iconImg.loading = 'lazy';
        optionDiv.appendChild(iconImg);
      }

      // Text content (name + optional price)
      var textWrap = document.createElement('span');
      textWrap.className = 'custom-dropdown__option-text';

      var nameSpan = document.createElement('span');
      nameSpan.className = 'custom-dropdown__option-name';
      nameSpan.textContent = optName;
      textWrap.appendChild(nameSpan);

      if (priceMatch) {
        var priceSpan = document.createElement('span');
        priceSpan.className = 'custom-dropdown__option-price';
        priceSpan.textContent = priceMatch[1];
        textWrap.appendChild(priceSpan);
      }

      optionDiv.appendChild(textWrap);

      if (opt.dataset.optionName) optionDiv.dataset.optionName = opt.dataset.optionName;
      if (opt.dataset.pricePence) optionDiv.dataset.pricePence = opt.dataset.pricePence;
      if (opt.dataset.handle)     optionDiv.dataset.handle     = opt.dataset.handle;

      optionDiv.addEventListener('click', function(e) {
        e.stopPropagation();

        select.value = this.dataset.value;
        var event = new Event('change', { bubbles: true });
        select.dispatchEvent(event);

        // Rebuild the trigger value as name + price (if priced) so the
        // surcharge stays visible next to the selection. Uses the same
        // .custom-dropdown__option-price class as the dropdown rows so
        // the pink styling carries over automatically.
        value.innerHTML = '';
        var displayName = this.querySelector('.custom-dropdown__option-name');
        var nameClone = document.createElement('span');
        nameClone.className = 'custom-dropdown__value-name';
        nameClone.textContent = displayName ? displayName.textContent : this.textContent;
        value.appendChild(nameClone);

        var srcPrice = this.querySelector('.custom-dropdown__option-price');
        if (srcPrice) {
          var priceClone = document.createElement('span');
          priceClone.className = 'custom-dropdown__option-price';
          priceClone.textContent = srcPrice.textContent;
          value.appendChild(priceClone);
        }

        dropdown.classList.add('has-value');
        dropdown.classList.remove('is-open', 'is-invalid');

        // Update trigger icon (icon-left layout)
        if (triggerImg) {
          if (imageUrl) {
            triggerImg.style.backgroundImage = 'url(' + imageUrl + ')';
            triggerImg.classList.add('has-image');
          } else {
            triggerImg.style.backgroundImage = '';
            triggerImg.classList.remove('has-image');
          }
        }

        // Update banner preview (banner layout)
        if (bannerPreview) {
          if (imageUrl) {
            bannerPreview.innerHTML = '<img src="' + imageUrl + '" alt="' + (optName || '') + '">';
            bannerPreview.style.display = '';
          } else {
            bannerPreview.innerHTML = '';
            bannerPreview.style.display = 'none';
          }
        }

        optionsPanel.querySelectorAll('.custom-dropdown__option').forEach(function(o) {
          o.classList.remove('is-selected');
        });
        this.classList.add('is-selected');
      });

      optionsPanel.appendChild(optionDiv);
    });

    dropdown.appendChild(trigger);
    if (bannerPreview) dropdown.appendChild(bannerPreview);
    dropdown.appendChild(optionsPanel);

    select.classList.add('custom-dropdown-hidden');
    field.appendChild(dropdown);

    trigger.addEventListener('click', function(e) {
      e.stopPropagation();
      document.querySelectorAll('.custom-dropdown.is-open').forEach(function(d) {
        if (d !== dropdown) d.classList.remove('is-open');
      });
      dropdown.classList.toggle('is-open');
    });

    var observer = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        if (mutation.attributeName === 'disabled') {
          if (select.disabled) {
            dropdown.classList.add('is-disabled');
          } else {
            dropdown.classList.remove('is-disabled');
          }
        }
      });
    });
    observer.observe(select, { attributes: true });
  }

  function initCustomDropdowns() {
    var selects = configuratorEl.querySelectorAll('.config-field select');
    if (selects.length === 0) return;

    selects.forEach(function(select) {
      buildSingleCustomDropdown(select);
    });

    document.addEventListener('click', function() {
      document.querySelectorAll('.custom-dropdown.is-open').forEach(function(d) {
        d.classList.remove('is-open');
      });
    });
  }

  // ============================================================
  // PROGRESSIVE FIELDS
  // ============================================================
  //
  // Cascade unlock — every <select> starts disabled except the first, and
  // each one unlocks the next when a value is selected. Same pattern as the
  // iron / driver configurator.
  //
  // Notes specific to L.A.B.:
  //   - The Personalisation Text <input> is NOT a select, so it's skipped
  //     by the cascade. It's already shown / hidden by togglePersonalisation
  //     Text() based on Laser = Yes, so leaving it out of the cascade is fine.
  //   - The Acknowledgement checkbox sits in a .config-field too but isn't
  //     a select, so it's also skipped by this function. Add-to-Basket is
  //     gated on it separately via isFormReady().
  //   - We only cascade fields that are visible on this product. Conditional
  //     fields (alignment back, shaft lean) that aren't rendered for this
  //     putter are naturally absent from the NodeList.

  function initProgressiveFields() {
    var fields = configuratorEl.querySelectorAll('.config-field select');
    if (fields.length < 2) return;

    // Disable everything except the first field
    for (var i = 1; i < fields.length; i++) {
      fields[i].disabled = true;
    }

    // Each field unlocks the next when changed to a non-empty value.
    // Hidden fields (e.g. Shaft Lean on combos with empty shaft_lean_options)
    // are transparent to the cascade: when the user picks a value on a
    // visible field, we walk forward through any hidden fields to enable
    // the next visible one. Hidden fields are also enabled along the way
    // so that if they become visible later (e.g. user switches putting
    // style back), they're already in the right cascade state.
    fields.forEach(function(field, index) {
      field.addEventListener('change', function() {
        if (!this.value) return;
        for (var k = index + 1; k < fields.length; k++) {
          fields[k].disabled = false;
          var wrapper = fields[k].closest('.config-field');
          if (!wrapper || wrapper.style.display !== 'none') break;
        }
      });
    });
  }

  // ============================================================
  // COMBO RULES ENGINE
  // ============================================================
  //
  // Reads template.combos JSON (per-template rules engine) and applies
  // putting-style + shaft-lean-driven filtering to the configurator fields.
  //
  // Schema (per combo entry):
  //   {
  //     "putting_style": "Standard|Counterbalance|Armlock|Sweeper",  // optional
  //     "price": 0|70|80,
  //     "default_length": "28",
  //     "default_lie": "63°",
  //     "default_shaft_handle": "matte-black",
  //     "grip_handles": [...],                              // combo-level grips
  //     "shaft_lean_options": [                             // optional
  //       {
  //         "value": "0°",
  //         "label": "0°",                                  // optional display label
  //         "grip_handles": [...]                           // optional grip override
  //       }
  //     ]
  //   }
  //
  // Matching rule: combo matches when (combo.putting_style || '') equals the
  // current putting style name (or '' if none selected). Putters with no
  // combos JSON (MEZZ.1, LINK family) skip this entirely. Putters with
  // combos but no putting_style keys (OZ.1i HS) match the single
  // "no putting style" combo at all times.
  //
  // Behaviour:
  //   - On Putting Style change → find matching combo → apply defaults
  //     (length, lie, shaft) → rebuild Shaft Lean dropdown → filter Grip
  //   - On Shaft Lean change → re-filter Grip dropdown (handles the
  //     OZ family Standard 0°/2° non-Press/Press flip)
  //   - On init → if a combo matches the initial state (e.g. OZ.1i HS),
  //     apply it immediately so defaults pre-fill

  var LAB_COMBOS = JSON.parse(configuratorEl.dataset.labCombos || '[]');

  function findCombo(puttingStyleName) {
    if (LAB_COMBOS.length === 0) return null;
    var target = puttingStyleName || '';
    for (var i = 0; i < LAB_COMBOS.length; i++) {
      if ((LAB_COMBOS[i].putting_style || '') === target) return LAB_COMBOS[i];
    }
    return null;
  }

  // Programmatically select an option in a custom dropdown by triggering its
  // click handler. Avoids CSS.escape concerns by iterating option divs and
  // matching the dataset.value property directly.
  //
  // Enables the native select first if it's disabled by the progressive
  // cascade. Without this, fields set by the combo engine stay visually
  // greyed (.custom-dropdown.is-disabled), and downstream fields don't
  // unlock because the cascade depends on each field being enabled when
  // its change event fires.
  function setDropdownByValue(selector, value) {
    if (!value) return false;
    var native = document.querySelector(selector);
    if (!native) return false;
    var field = native.closest('.config-field');
    if (!field) return false;
    var dropdown = field.querySelector('.custom-dropdown');
    if (!dropdown) return false;

    if (native.disabled) native.disabled = false;

    var optionDivs = dropdown.querySelectorAll('.custom-dropdown__option');
    for (var i = 0; i < optionDivs.length; i++) {
      if (optionDivs[i].dataset.value === value) {
        optionDivs[i].click();
        return true;
      }
    }
    return false;
  }

  // Look up a shaft option by handle (data-handle attribute) and select it
  // via its native value.
  function setShaftByHandle(handle) {
    if (!handle) return false;
    var shaftSelect = document.querySelector('[data-shaft-option]');
    if (!shaftSelect) return false;
    var opts = shaftSelect.options;
    for (var i = 0; i < opts.length; i++) {
      if (opts[i].dataset.handle === handle) {
        return setDropdownByValue('[data-shaft-option]', opts[i].value);
      }
    }
    return false;
  }

  // Filter a custom dropdown to show only options whose data-handle is in
  // allowedHandles. Pass null to show all. If the currently-selected option
  // becomes hidden, the selection is cleared and the user must pick again.
  function filterDropdownByHandles(selector, allowedHandles) {
    var native = document.querySelector(selector);
    if (!native) return;
    var field = native.closest('.config-field');
    if (!field) return;
    var dropdown = field.querySelector('.custom-dropdown');
    if (!dropdown) return;

    var allowSet = null;
    if (allowedHandles) {
      allowSet = {};
      for (var i = 0; i < allowedHandles.length; i++) allowSet[allowedHandles[i]] = true;
    }

    var currentValue = native.value;
    var currentHidden = false;

    // Filter native options
    for (var j = 0; j < native.options.length; j++) {
      var opt = native.options[j];
      if (!opt.value) continue; // skip placeholder
      var handle = opt.dataset.handle;
      if (allowSet === null || (handle && allowSet[handle])) {
        opt.hidden = false;
        opt.disabled = false;
      } else {
        opt.hidden = true;
        opt.disabled = true;
        if (opt.value === currentValue) currentHidden = true;
      }
    }

    // Filter visual option divs in sync
    var optionDivs = dropdown.querySelectorAll('.custom-dropdown__option');
    for (var k = 0; k < optionDivs.length; k++) {
      var div = optionDivs[k];
      var divHandle = div.dataset.handle;
      if (allowSet === null || (divHandle && allowSet[divHandle])) {
        div.style.display = '';
      } else {
        div.style.display = 'none';
      }
    }

    // If the current selection got filtered out, reset
    if (currentHidden) {
      native.value = '';
      var valueEl = dropdown.querySelector('.custom-dropdown__value');
      if (valueEl) valueEl.textContent = '';
      dropdown.classList.remove('has-value');
      var triggerImg = dropdown.querySelector('.custom-dropdown__trigger-icon');
      if (triggerImg) { triggerImg.style.backgroundImage = ''; triggerImg.classList.remove('has-image'); }
      var banner = dropdown.querySelector('.custom-dropdown__banner-preview');
      if (banner) { banner.innerHTML = ''; banner.style.display = 'none'; }
      dropdown.querySelectorAll('.custom-dropdown__option.is-selected').forEach(function(d) {
        d.classList.remove('is-selected');
      });
      native.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  // Rebuild the Shaft Lean dropdown from a combo's shaft_lean_options array.
  // Preserves the custom-dropdown structure so visual state stays intact.
  function rebuildShaftLeanOptions(slOptions) {
    var select = document.querySelector('[data-custom-shaft-lean]');
    if (!select) return;
    var field = select.closest('.config-field');
    if (!field) return;
    var dropdown = field.querySelector('.custom-dropdown');
    if (!dropdown) return;
    var optionsPanel = dropdown.querySelector('.custom-dropdown__options');
    if (!optionsPanel) return;

    // Clear native options (preserve placeholder)
    var toRemove = [];
    for (var i = 0; i < select.options.length; i++) {
      if (select.options[i].value) toRemove.push(select.options[i]);
    }
    toRemove.forEach(function(o) { o.remove(); });

    // Clear visual option divs
    optionsPanel.innerHTML = '';

    // Reset selection state on the dropdown
    select.value = '';
    var valueEl = dropdown.querySelector('.custom-dropdown__value');
    if (valueEl) valueEl.textContent = '';
    dropdown.classList.remove('has-value');

    if (!slOptions || slOptions.length === 0) {
      field.style.display = 'none';
      return;
    }
    field.style.display = '';

    // Rebuild options
    slOptions.forEach(function(o) {
      var nativeOpt = document.createElement('option');
      nativeOpt.value = o.value;
      nativeOpt.textContent = o.label || o.value;
      select.appendChild(nativeOpt);

      var optDiv = document.createElement('div');
      optDiv.className = 'custom-dropdown__option';
      optDiv.dataset.value = o.value;
      var textWrap = document.createElement('span');
      textWrap.className = 'custom-dropdown__option-text';
      var nameSpan = document.createElement('span');
      nameSpan.className = 'custom-dropdown__option-name';
      nameSpan.textContent = o.label || o.value;
      textWrap.appendChild(nameSpan);
      optDiv.appendChild(textWrap);

      optDiv.addEventListener('click', function(e) {
        e.stopPropagation();
        select.value = this.dataset.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        if (valueEl) valueEl.textContent = this.querySelector('.custom-dropdown__option-name').textContent;
        dropdown.classList.add('has-value');
        dropdown.classList.remove('is-open', 'is-invalid');
        optionsPanel.querySelectorAll('.custom-dropdown__option').forEach(function(d) {
          d.classList.remove('is-selected');
        });
        this.classList.add('is-selected');
      });

      optionsPanel.appendChild(optDiv);
    });

    // Don't auto-select — user must pick shaft lean themselves.
    // Fire a change event so state.shaft_lean clears (the value was reset
    // to '' above, but wireDropdown's syncFromElement only fires on change).
    select.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function applyCombo(combo) {
    if (!combo) {
      // No combo matched — clear any combo-imposed filters
      filterDropdownByHandles('[data-grip-option]', null);
      filterDropdownByHandles('[data-shaft-option]', null);
      return;
    }

    // Filter Shaft dropdown to combo-specific handles. Like grips, this
    // hides options whose handle isn't in shaft_handles; selected value
    // resets if it gets filtered out.
    if (combo.shaft_handles) {
      filterDropdownByHandles('[data-shaft-option]', combo.shaft_handles);
    }

    // Rebuild Length dropdown if combo specifies a length list.
    // Supports both plain string values and {value, label} objects
    // (the latter for Sweeper "(may show shaft discolour)" labels).
    if (combo.length_options) {
      rebuildLengthOptions(combo.length_options);
    }

    // Rebuild Lie dropdown if combo specifies a lie list.
    if (combo.lie_options) {
      rebuildLieOptions(combo.lie_options);
    }

    // Rebuild Shaft Lean dropdown for this combo. Empty array hides the
    // field; absence leaves the field in its previous state.
    // Does NOT auto-select — user picks shaft lean themselves.
    if (combo.shaft_lean_options !== undefined) {
      rebuildShaftLeanOptions(combo.shaft_lean_options);
    }

    // Apply grip filter based on current shaft lean (or the union if none
    // selected yet — see applyShaftLeanRule).
    applyShaftLeanRule(combo, state.shaft_lean);
  }

  function applyShaftLeanRule(combo, shaftLeanValue) {
    if (!combo) {
      filterDropdownByHandles('[data-grip-option]', null);
      return;
    }

    // Find shaft-lean-specific grip override (if any)
    var slOpt = null;
    if (combo.shaft_lean_options && shaftLeanValue) {
      for (var i = 0; i < combo.shaft_lean_options.length; i++) {
        if (combo.shaft_lean_options[i].value === shaftLeanValue) {
          slOpt = combo.shaft_lean_options[i];
          break;
        }
      }
    }

    // Grip list precedence:
    //   1. Selected shaft-lean's grip_handles (most specific — e.g. OZ.1i
    //      Standard 0° → non-Press 6, 2° → Press 6)
    //   2. Combo's top-level grip_handles (e.g. DF2.1 Standard → Press 6)
    //   3. Union of all shaft_lean_options' grip_handles (when shaft lean
    //      not yet selected on a combo that varies grips by shaft lean —
    //      OZ.1i Standard pre-shaft-lean shows all 12 Std grips, narrowing
    //      to 6 once the user picks)
    //   4. null — no filtering, show all grips on the product (shouldn't
    //      happen on combo putters but defensive)
    var grips = null;
    if (slOpt && slOpt.grip_handles) {
      grips = slOpt.grip_handles;
    } else if (combo.grip_handles) {
      grips = combo.grip_handles;
    } else if (combo.shaft_lean_options) {
      var union = {};
      combo.shaft_lean_options.forEach(function(o) {
        if (o.grip_handles) {
          o.grip_handles.forEach(function(h) { union[h] = true; });
        }
      });
      var unionList = Object.keys(union);
      if (unionList.length > 0) grips = unionList;
    }

    filterDropdownByHandles('[data-grip-option]', grips);
  }

  // Wire combo triggers — hook into existing change events on putting style
  // and shaft lean dropdowns. wireDropdown already updates state.putting_style
  // and state.shaft_lean for us; this just adds the filtering layer.
  function initComboEngine() {
    if (LAB_COMBOS.length === 0) return; // static putter — no combos

    var psSelect = document.querySelector('[data-custom-putting-style]');
    if (psSelect) {
      psSelect.addEventListener('change', function() {
        var combo = findCombo(state.putting_style && state.putting_style.name);
        applyCombo(combo);
      });
    }

    var slSelect = document.querySelector('[data-custom-shaft-lean]');
    if (slSelect) {
      slSelect.addEventListener('change', function() {
        var combo = findCombo(state.putting_style && state.putting_style.name);
        applyShaftLeanRule(combo, state.shaft_lean);
      });
    }

    // On init: if there's a combo with no putting_style requirement (e.g.
    // OZ.1i HS where combos has one entry with no putting_style key), apply
    // it immediately so defaults pre-fill.
    var initialCombo = findCombo('');
    if (initialCombo) {
      applyCombo(initialCombo);
    }
  }

  // ============================================================
  // GENERIC OPTION REBUILD (length / lie)
  // ============================================================
  //
  // Builds a simple text-only dropdown's options from a values array.
  // Each entry can be a plain string ("28") or an object ({value, label}).
  // The label form is used for length values that need a trailing note
  // like "(may show shaft discolour)" while keeping the underlying value
  // numeric. Same DOM pattern as rebuildShaftLeanOptions — preserves the
  // custom-dropdown structure.
  //
  // Used by the combo engine to rebuild Length and Lie dropdowns when
  // putting style changes — the per-combo length_options and lie_options
  // arrays override the template-level pool.

  function rebuildSimpleSelectOptions(selector, values, valueSuffix) {
    var select = document.querySelector(selector);
    if (!select) return;
    var field = select.closest('.config-field');
    if (!field) return;
    var dropdown = field.querySelector('.custom-dropdown');
    if (!dropdown) return;
    var optionsPanel = dropdown.querySelector('.custom-dropdown__options');
    if (!optionsPanel) return;

    var suffix = valueSuffix || '';

    // Skip if already matching to avoid unnecessary churn + spurious change event
    var currentValues = Array.prototype.slice.call(select.options)
      .filter(function(o) { return o.value; })
      .map(function(o) { return o.value; });
    var nextValues = values.map(function(v) { return (typeof v === 'object') ? v.value : v; });
    if (currentValues.length === nextValues.length &&
        currentValues.every(function(v, i) { return v === nextValues[i]; })) {
      return;
    }

    // Clear native options (preserve placeholder)
    var toRemove = [];
    for (var i = 0; i < select.options.length; i++) {
      if (select.options[i].value) toRemove.push(select.options[i]);
    }
    toRemove.forEach(function(o) { o.remove(); });

    // Clear visual option divs
    optionsPanel.innerHTML = '';

    // Reset selection state
    select.value = '';
    var valueEl = dropdown.querySelector('.custom-dropdown__value');
    if (valueEl) valueEl.textContent = '';
    dropdown.classList.remove('has-value');

    if (!values || values.length === 0) {
      field.style.display = 'none';
      select.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }
    field.style.display = '';

    values.forEach(function(v) {
      var val = (typeof v === 'object') ? v.value : v;
      var displayLabel = (typeof v === 'object' && v.label) ? v.label : (val + suffix);

      var nativeOpt = document.createElement('option');
      nativeOpt.value = val;
      nativeOpt.textContent = displayLabel;
      select.appendChild(nativeOpt);

      var optDiv = document.createElement('div');
      optDiv.className = 'custom-dropdown__option';
      optDiv.dataset.value = val;
      var textWrap = document.createElement('span');
      textWrap.className = 'custom-dropdown__option-text';
      var nameSpan = document.createElement('span');
      nameSpan.className = 'custom-dropdown__option-name';
      nameSpan.textContent = displayLabel;
      textWrap.appendChild(nameSpan);
      optDiv.appendChild(textWrap);

      optDiv.addEventListener('click', function(e) {
        e.stopPropagation();
        select.value = this.dataset.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        if (valueEl) valueEl.textContent = this.querySelector('.custom-dropdown__option-name').textContent;
        dropdown.classList.add('has-value');
        dropdown.classList.remove('is-open', 'is-invalid');
        optionsPanel.querySelectorAll('.custom-dropdown__option').forEach(function(d) {
          d.classList.remove('is-selected');
        });
        this.classList.add('is-selected');
      });

      optionsPanel.appendChild(optDiv);
    });

    // Fire change so state.length / state.lie clears (user re-picks within the new range)
    select.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function rebuildLengthOptions(values) {
    // Length values render with a trailing inch mark; labels (e.g. discolour
    // suffix) override that
    rebuildSimpleSelectOptions('[data-custom-length]', values, '"');
  }

  function rebuildLieOptions(values) {
    // Lie values render exactly as the data (e.g. "63°") — values already
    // include the degree symbol
    rebuildSimpleSelectOptions('[data-custom-lie]', values, '');
  }


  // ============================================================
  // INIT
  // ============================================================

  function init() {
    initTabs();
    initOptionSelection();
    initPersonalisationText();
    initAcknowledgement();
    initProgressiveFields();
    initCustomDropdowns();
    initComboEngine();

    var addBtn = document.querySelector('[data-custom-fit-add]');
    if (addBtn) {
      addBtn.addEventListener('click', function(e) { e.preventDefault(); addToCart(); });
    }

    var hasTabs = document.querySelectorAll('[data-tab]').length > 0;
    var customPanel = document.querySelector('[data-tab-panel="custom"]');
    if (hasTabs) {
      if (customPanel) customPanel.style.display = 'none';
    } else {
      isCustomTab = true;
      document.body.classList.add('configurator-custom-active');
      document.querySelectorAll('.product-info__buy-buttons-wrapper, .product-info__variant-picker, .product-info__inventory-status-wrapper, .product-info__estimate-delivery-wrapper').forEach(function(el) {
        el.style.display = 'none';
      });
      document.querySelectorAll('.product-info__text-with-icon_wrapper').forEach(function(el) {
        var text = el.textContent.toLowerCase();
        if (text.indexOf('in stock') > -1 || text.indexOf('dispatched within') > -1) {
          el.style.display = 'none';
          el.dataset.hiddenByConfigurator = 'true';
        }
      });
      updatePriceDisplay();
    }

    if (sessionStorage.getItem('openCartDrawer') === 'true') {
      sessionStorage.removeItem('openCartDrawer');
      setTimeout(function() {
        if (typeof Alpine !== 'undefined') {
          Alpine.store('xMiniCart').handleOpen();
        }
      }, 500);
    }

    updatePriceDisplay();
    console.log('L.A.B. Configurator initialised', CONFIG);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
